import React from 'react';
import { StarIcon } from '@heroicons/react/24/solid';

export default function Hero() {
  return (
    <section className="relative bg-gradient-to-b from-indigo-50/60 via-white to-white overflow-hidden pt-12 pb-0">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-100 rounded-full opacity-40 blur-3xl" />
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-100 rounded-full opacity-30 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top badge */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 shadow-sm text-sm text-gray-600">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} className="w-3.5 h-3.5 text-yellow-400" />
              ))}
            </div>
            <span className="font-semibold text-gray-800">4.8</span>
            <span className="text-gray-400">|</span>
            <span>40,000 users</span>
          </div>
        </div>

        {/* Headline */}
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight tracking-tight mb-6">
            Find leads that{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-indigo-600">respond.</span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 10C50 4 150 2 298 6" stroke="#6366f1" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-500 mb-8 max-w-2xl mx-auto leading-relaxed">
            Win more deals with better data. Access 280M truly verified leads and companies.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-7 py-3.5 rounded-xl text-base shadow-lg shadow-indigo-200 transition-all hover:shadow-indigo-300 hover:-translate-y-0.5">
              Get started for free
            </a>
            <a href="#" className="w-full sm:w-auto border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold px-7 py-3.5 rounded-xl text-base transition-colors">
              Contact Sales
            </a>
          </div>
          <p className="mt-3 text-xs text-gray-400">No credit card required · 50 free credits to start</p>
        </div>

        {/* Dashboard Screenshot */}
        <div className="relative mt-12 mx-auto max-w-5xl">
          {/* Popup card */}
          <div className="absolute -top-4 -left-4 z-20 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-56 hidden sm:block animate-bounce-slow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">JW</div>
              <div>
                <p className="text-xs font-semibold text-gray-800">Josh Whitfield</p>
                <p className="text-xs text-gray-400">Founder at CMM</p>
              </div>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">"Every email, every phone number, it just works."</p>
            <div className="flex mt-1.5">
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} className="w-3 h-3 text-yellow-400" />
              ))}
            </div>
          </div>

          {/* Mock dashboard */}
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <div className="flex-1 mx-4 bg-gray-200 rounded-md h-5 max-w-xs" />
            </div>
            {/* Dashboard content */}
            <div className="p-6 bg-gray-50 min-h-64">
              <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Total Contacts', value: '12,847', color: 'indigo' },
                  { label: 'Verified Emails', value: '11,203', color: 'green' },
                  { label: 'Mobile Numbers', value: '4,561', color: 'blue' },
                  { label: 'Companies', value: '3,892', color: 'purple' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                    <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                    <div className={`mt-2 h-1 rounded-full bg-${stat.color}-200`}>
                      <div className={`h-1 rounded-full bg-${stat.color}-500 w-3/4`} />
                    </div>
                  </div>
                ))}
              </div>
              {/* Table rows */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="grid grid-cols-5 gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <span>Name</span><span>Title</span><span>Company</span><span>Email</span><span>Phone</span>
                </div>
                {[
                  { name: 'Sarah Johnson', title: 'VP Sales', company: 'Acme Corp', email: 's.john•••@acme.com', phone: '+1 (555) •••-4521' },
                  { name: 'Mark Stevens', title: 'CEO', company: 'TechStart', email: 'm.stev•••@tech.io', phone: '+44 7•••-234-567' },
                  { name: 'Priya Patel', title: 'Head of Growth', company: 'ScaleUp', email: 'p.pat•••@scale.co', phone: '+1 (415) •••-8932' },
                  { name: 'James Wren', title: 'CTO', company: 'DevHouse', email: 'j.wren•••@devh.io', phone: '+49 176 •••-1234' },
                ].map((row, i) => (
                  <div key={i} className="grid grid-cols-5 gap-4 px-4 py-3 text-sm text-gray-700 border-b border-gray-50 hover:bg-indigo-50/40 transition-colors">
                    <span className="font-medium text-gray-900">{row.name}</span>
                    <span className="text-gray-500">{row.title}</span>
                    <span>{row.company}</span>
                    <span className="text-indigo-600 font-mono text-xs">{row.email}</span>
                    <span className="text-gray-500 font-mono text-xs">{row.phone}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
